<template>
  <div class="carousel-management">
    <!-- 頁面標題 -->
    <div class="page-header">
      <h1>輪播圖管理</h1>
    </div>

    <!-- 輪播圖列表 -->
    <div class="carousel-list">
      <div
        v-for="carousel in carousels"
        :key="carousel.Id"
        class="carousel-card"
        :class="{ inactive: !carousel.IsActive }"
      >
        <!-- 卡片頂部 - 狀態 -->
        <div class="card-header-section">
          <div
            class="status-badge"
            :class="carousel.IsActive ? 'active' : 'inactive'"
          >
            {{ carousel.IsActive ? "✓ 已啟用" : "⏸ 已停用" }}
          </div>
        </div>

        <!-- 圖片預覽 -->
        <div class="image-preview">
          <img :src="carousel.ImageUrl" :alt="carousel.Name" />
          <div class="image-overlay">
            <button class="preview-btn" @click="previewCarousel(carousel)">
              預覽
            </button>
          </div>
        </div>

        <!-- 輪播圖資訊 -->
        <div class="carousel-info">
          <h3 class="carousel-title">{{ carousel.Name }}</h3>
          <p class="carousel-subtitle" v-if="carousel.Description">
            {{ carousel.Description }}
          </p>
          <div class="carousel-meta">
            <div class="meta-item">
              <!-- <span class="meta-label">連結：</span>
              <span class="meta-value">
                {{ carousel.ImageUrl || "無" }}
              </span> -->
            </div>
            <div class="meta-item">
              <span class="meta-label">建立時間：</span>
              <span class="meta-value">
                {{
                  carousel.CreateDate ? formatDate(carousel.CreateDate) : "—"
                }}</span
              >
            </div>
            <div class="meta-item">
              <span class="meta-label">最後更新：</span>
              <span class="meta-value">
                {{
                  carousel.UpdateDate ? formatDate(carousel.UpdateDate) : "—"
                }}
              </span>
            </div>
            <div class="meta-item">
              <!-- 活動開始時間外層區塊 -->
              <span class="meta-label">活動開始時間：</span>
              <!-- 標籤文字顯示「活動開始時間」 -->
              <span class="meta-value">
                {{ carousel.StartTime ? formatDate(carousel.StartTime) : "—" }}
                <!-- 有值就用 formatDate 格式化，沒有就顯示破折號 -->
              </span>
            </div>

            <div class="meta-item">
              <!-- 活動結束時間外層區塊 -->
              <span class="meta-label">活動結束時間：</span>
              <!-- 標籤文字顯示「活動結束時間」 -->
              <span class="meta-value">
                {{ carousel.EndTime ? formatDate(carousel.EndTime) : "—" }}
                <!-- 有值就用 formatDate 格式化，沒有就顯示破折號 -->
              </span>
            </div>
          </div>
        </div>

        <!-- 操作按鈕 -->
        <div class="card-actions">
          <button
            class="btn btn-secondary btn-sm"
            @click="editCarousel(carousel)"
          >
            編輯
          </button>
          <button
            class="btn btn-toggle btn-sm"
            @click="toggleActive(carousel)"
            :class="carousel.IsActive ? 'btn-warning' : 'btn-success'"
          >
            {{ carousel.IsActive ? "⏸ 停用" : "▶ 啟用" }}
          </button>
        </div>
      </div>

      <!-- 空狀態 -->
      <div v-if="carousels.length === 0" class="empty-state">
        <div class="empty-icon">🖼️</div>
        <h3>尚無輪播圖</h3>
        <p>點擊右上角「新增輪播圖」按鈕開始建立</p>
      </div>
    </div>

    <!-- 新增/編輯輪播圖 Modal -->
    <div v-if="showModal" class="modal-overlay">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>{{ editingCarousel ? "編輯輪播圖" : "新增輪播圖" }}</h3>
          <button class="close-btn" @click="closeModal">×</button>
        </div>

        <div class="modal-body">
          <!-- 左側 -->
          <div class="left-column">
            <!-- 標題 -->
            <div class="form-group">
              <label class="form-label">標題 *</label>
              <input
                type="text"
                class="form-input"
                v-model="formData.title"
                placeholder="請輸入輪播圖標題"
                required
              />
            </div>

            <!-- 副標題 -->
            <div class="form-group">
              <label class="form-label">副標題</label>
              <input
                type="text"
                class="form-input"
                v-model="formData.subtitle"
                placeholder="請輸入副標題（選填）"
              />
            </div>

            <!-- 活動開始時間 -->
            <div class="form-group">
              <!-- 活動開始欄位區塊 -->
              <label class="form-label">活動開始時間 *</label>
              <!-- 欄位名稱 -->
              <input
                type="datetime-local"
                class="form-input"
                v-model="formData.startTime"
                required
              />
              <!-- 日期時間輸入 -->
            </div>

            <!-- 活動結束時間 -->
            <div class="form-group">
              <!-- 活動結束欄位區塊 -->
              <label class="form-label">活動結束時間 *</label>
              <!-- 欄位名稱 -->
              <input
                type="datetime-local"
                class="form-input"
                v-model="formData.endTime"
                required
              />
              <!-- 日期時間輸入 -->
            </div>
            <!-- 連結 -->
            <!-- <div class="form-group">
              <label class="form-label">連結網址</label>
              <input
                type="url"
                class="form-input"
                v-model="formData.link"
                placeholder="https://example.com（選填）"
              />
              <p class="field-hint">點擊輪播圖時的跳轉網址</p>
            </div> -->

            <!-- 啟用狀態 -->
            <div class="form-group">
              <label class="form-label">啟用狀態</label>
              <div class="status-toggle">
                <label class="toggle-switch">
                  <input type="checkbox" v-model="formData.active" />
                  <span class="toggle-slider"></span>
                </label>
                <span class="toggle-label">
                  {{ formData.active ? "已啟用" : "已停用" }}
                </span>
              </div>
            </div>
          </div>

          <!-- 右側 -->
          <div class="right-column">
            <!-- 圖片上傳 -->
            <div class="form-group">
              <label class="form-label">輪播圖片 *</label>

              <div class="image-upload-area" @click="triggerFileInput">
                <input
                  type="file"
                  ref="imageInput"
                  accept="image/jpeg,image/png,image/jpg,image/webp"
                  @change="handleImageUpload"
                  style="display: none"
                />

                <div v-if="formData.imagePreview" class="image-preview-large">
                  <img :src="formData.imagePreview" alt="預覽圖" />
                  <button
                    type="button"
                    class="remove-image-btn"
                    @click.stop="removeImage"
                  >
                    ✕ 移除
                  </button>
                </div>

                <div v-else class="upload-placeholder">
                  <span class="upload-icon">📷</span>
                  <p>點擊上傳輪播圖片</p>
                  <span class="upload-hint">建議尺寸：1920x600 像素</span>
                </div>
              </div>

              <div class="image-requirements">
                <p class="requirement-title">📋 圖片規範：</p>
                <ul class="requirement-list">
                  <li>✓ 格式:JPG、JPEG、PNG、WebP</li>
                  <li>✓ 尺寸:建議 1600x900 像素</li>
                  <li>✓ 檔案大小:不超過 2MB</li>
                  <li>✓ 適用於電腦、平板、手機等裝置</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeModal">取消</button>
          <button class="btn btn-primary" @click="saveCarousel">
            {{ editingCarousel ? "儲存變更" : "新增輪播圖" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 預覽 Modal -->
    <div
      v-if="showPreviewModal"
      class="modal-overlay"
      @click="showPreviewModal = false"
    >
      <div class="modal preview-modal" @click.stop>
        <div class="modal-header">
          <h3>輪播圖預覽</h3>
          <button class="close-btn" @click="showPreviewModal = false">×</button>
        </div>

        <div class="preview-body">
          <div class="preview-container">
            <img :src="previewImage" :alt="previewTitle" />
            <div class="preview-info" v-if="previewTitle || previewSubtitle">
              <h2 v-if="previewTitle">{{ previewTitle }}</h2>
              <p v-if="previewSubtitle">{{ previewSubtitle }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script src="@/assets/js/banner.js"></script>
<style scoped src="@/assets/css/banner.css"></style>
